import { TMSEquipmentType } from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

// Tenant-agnostic AI suggestion maps for equipment. Reference qualifiers are
// resolved by the backend against the integration's reference types (ENG-9580);
// the equipment map is next to move the same way.
export type MGBSuggestionMaps = {
  transportTypeMap: Record<string, string>;
  defaultEquipmentName: string;
};

// AI suggested transport type → Kenco MGB equipment display name.
export const KENCO_MGB_TRANSPORT_TYPE_TO_EQUIPMENT_NAME: Record<
  string,
  string
> = {
  'cargo van': 'Cargo Van - Sprinter',
  'straight truck': 'Straight Truck - Dry',
  'dry van': 'Van - Dry',
  reefer: 'Van - Refrigerated',
  van: 'Van - Dry',
  flatbed: 'Flatbed',
};

export const KENCO_MGB_DEFAULT_EQUIPMENT_NAME = 'Van - Dry';

export const KENCO_MGB_SUGGESTION_MAPS: MGBSuggestionMaps = {
  transportTypeMap: KENCO_MGB_TRANSPORT_TYPE_TO_EQUIPMENT_NAME,
  defaultEquipmentName: KENCO_MGB_DEFAULT_EQUIPMENT_NAME,
};

// Maps an AI transport type string to an equipment display name.
// Empty input stays empty (do not invent equipment when AI omitted it).
// Unknown non-empty values fall back to defaultEquipmentName.
export function mapTransportTypeToEquipmentName(
  transportType: string | undefined,
  transportTypeMap: Record<string, string>,
  defaultEquipmentName: string
): string {
  const trimmed = (transportType ?? '').trim();
  if (!trimmed) {
    return '';
  }

  return transportTypeMap[trimmed.toLowerCase()] ?? defaultEquipmentName;
}

function findEquipmentByName(
  equipmentTypes: TMSEquipmentType[],
  targetName: string
): Maybe<TMSEquipmentType> {
  const normalizedTarget = targetName.trim().toLowerCase();
  if (!normalizedTarget) {
    return null;
  }

  return (
    equipmentTypes.find(
      (eq) => (eq.name?.trim() ?? '').toLowerCase() === normalizedTarget
    ) ?? null
  );
}

export function splitMGEquipmentLabel(raw: string): {
  label: string;
  code: string;
} {
  const trimmed = raw.trim();
  const match = trimmed.match(/\(([A-Za-z0-9]+)\)\s*$/);
  if (!match) {
    return { label: trimmed, code: '' };
  }
  return {
    label: trimmed.slice(0, match.index).trim(),
    code: match[1] ?? '',
  };
}

export function hasMGEquipmentParenCode(raw: string | undefined): boolean {
  return Boolean(splitMGEquipmentLabel(raw ?? '').code);
}

/**
 * Resolve an AI / form transport type value onto a customer-scoped MGB
 * equipment option (id + name). Empty raw → null. Prefer exact id/name match,
 * then parenthetical MG codes in stored labels (e.g. "Van - Dry (TF)"),
 * then the tenant transport→name map (and that tenant's default).
 */
export function resolveMGBEquipment(
  raw: string | undefined,
  equipmentTypes: MaybeUndef<TMSEquipmentType[]>,
  maps: Pick<MGBSuggestionMaps, 'transportTypeMap' | 'defaultEquipmentName'>
): Maybe<{ id: string; name: string }> {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return null;
  }

  const options = (equipmentTypes ?? []).filter((eq) => Boolean(eq.id?.trim()));
  if (options.length === 0) {
    return null;
  }

  const byId = options.find(
    (eq) => (eq.id?.trim() ?? '').toLowerCase() === trimmed.toLowerCase()
  );
  if (byId) {
    return { id: byId.id.trim(), name: byId.name?.trim() ?? '' };
  }

  const { label: labelWithoutCode, code: parenCode } =
    splitMGEquipmentLabel(trimmed);
  if (parenCode) {
    const byParenCode = options.find(
      (eq) => (eq.id?.trim() ?? '').toLowerCase() === parenCode.toLowerCase()
    );
    if (byParenCode) {
      return {
        id: byParenCode.id.trim(),
        name: byParenCode.name?.trim() ?? '',
      };
    }
  }

  const byName =
    findEquipmentByName(options, trimmed) ??
    (labelWithoutCode !== trimmed
      ? findEquipmentByName(options, labelWithoutCode)
      : null);

  if (byName) {
    return { id: byName.id.trim(), name: byName.name?.trim() ?? '' };
  }

  const mappedName = mapTransportTypeToEquipmentName(
    trimmed,
    maps.transportTypeMap,
    maps.defaultEquipmentName
  );
  const mapped = findEquipmentByName(options, mappedName);
  if (mapped) {
    return { id: mapped.id.trim(), name: mapped.name?.trim() ?? '' };
  }

  if (
    mappedName.toLowerCase() !== maps.defaultEquipmentName.trim().toLowerCase()
  ) {
    const fallback = findEquipmentByName(options, maps.defaultEquipmentName);
    if (fallback) {
      return { id: fallback.id.trim(), name: fallback.name?.trim() ?? '' };
    }
  }

  return null;
}
